// @flow
import React, { Component } from 'react';
import Chartist from 'chartist';
import ChartistGraph from 'react-chartist';

import { PrimaryButton } from '../../../Buttons.jsx';

import lengthWeight from './constants/wtleninf';

type Props = {
  measurements: Object[],
  sex: string,
}

type State = {
  weightUnit: string,
  lengthUnit: string,
}

class InfantWeightLengthChart extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      weightUnit: 'kg',
      lengthUnit: 'cm',
    };
  }

  toggleUnits = () => {
    this.setState({
      weightUnit: (this.state.weightUnit === 'lb' ? 'kg' : 'lb'),
      lengthUnit: (this.state.lengthUnit === 'in' ? 'cm' : 'in'),
    });
  }

  // needed to re-render based on unit properly for Chartist plugin
  getXAxis = () => `Length (${this.state.lengthUnit})`;

  getYAxis = () => `Weight (${this.state.weightUnit})`;

  render() {
    const KG_TO_LB = 2.2;
    const CM_TO_IN = 2.54;

    const { weightUnit, lengthUnit } = this.state;

    const measurements = this.props.measurements.slice()
      // eslint-disable-next-line no-restricted-globals
      .filter(x => !isNaN(x.length))
      .sort((a, b) => a.length - b.length)
      .map((item) => {
        if (!item.length || !item.weight) {
          return {};
        }
        const x = (lengthUnit === 'cm' ? item.length : item.length && (item.length / CM_TO_IN).toFixed(1));
        const y = (weightUnit === 'kg' ? item.weight : item.weight && (item.weight * KG_TO_LB).toFixed(1));
        const percentile = item.lwPercentile;
        return {
          value: { x, y },
          // string for popover text
          meta: `Length: ${x} ${lengthUnit}, Weight: ${y} ${weightUnit} (${percentile}%)`,
        };
      });

    const {
      p3, p10, p25, p50, p75, p90, p97,
    } = lengthWeight[this.props.sex];
    const percentiles = [p3, p10, p25, p50, p75, p90, p97];
    const labels = ['3', '10', '25', '50', '75', '90', '97'];
    for (let i = 0; i < percentiles.length; i++) {
      percentiles[i] = percentiles[i].map((val, index) => ({
        x: (lengthUnit === 'cm' ? (index === 0 ? 45 : index + 44.5) : (index === 0 ? 45 : index + 44.5) / CM_TO_IN),
        y: (weightUnit === 'kg' ? val : val * KG_TO_LB),
        label: labels[i],
      }));
    }
    // plot single points at end of line to have labels for percentile lines
    const percentileLabels = percentiles.map(p => ({
      name: 'percentileLabels',
      data: [p[p.length - 1]],
    }));

    const data = {
      series: [...percentiles, ...percentileLabels,
        {
          name: 'measurements',
          data: measurements,
        },
      ],
    };

    const options = {
      series: {
        measurements: {
          showPoint: true,
          lineSmooth: Chartist.Interpolation.none({
            fillHoles: true,
          }),
        },
        percentileLabels: {
          showPoint: true,
        },
      },
      axisX: {
        labelOffset: {
          x: -7,
          y: 0,
        },
        type: Chartist.FixedScaleAxis,
        high: (lengthUnit === 'cm' ? 105 : 41),
        low: (lengthUnit === 'cm' ? 45 : 18),
        divisor: (lengthUnit === 'cm' ? 60 : 23),
        // don't interpolate if unit is in (display each line), else every 5 if cm
        labelInterpolationFnc: value => (value % 5 === 0 || lengthUnit === 'in' ? value : ''),
      },
      axisY: {
        labelOffset: {
          x: 0,
          y: 7,
        },
        type: Chartist.FixedScaleAxis,
        high: (weightUnit === 'kg' ? 20 : 44),
        low: 0,
        divisor: (weightUnit === 'kg' ? 20 : 44),
        // don't interpolate if unit is kg (display each line), else every 2 if lb
        labelInterpolationFnc: value => (value % 2 === 0 || weightUnit === 'kg' ? value : ''),
      },
      showPoint: false,
      height: '85vh',
      chartPadding: {
        bottom: 10,
        left: 10,
      },
      plugins: [
        Chartist.plugins.ctAxisTitle({
          axisX: {
            axisTitle: this.getXAxis,
            offset: {
              x: 0,
              y: 35,
            },
          },
          axisY: {
            axisTitle: this.getYAxis,
            offset: {
              x: 0,
              y: 10,
            },
            flipTitle: true,
          },
        }),
        Chartist.plugins.tooltip(),
        Chartist.plugins.ctPointLabels({
          textAnchor: 'start',
          labelOffset: {
            x: 3,
            y: 4,
          },
        }),
      ],
    };

    return (
      <div>
        <PrimaryButton onClick={this.toggleUnits}>Toggle Units</PrimaryButton>
        <ChartistGraph data={data} options={options} type={'Line'} />
      </div>
    );
  }
}

export default InfantWeightLengthChart;



// WEBPACK FOOTER //
// ./client/src/components/patientPages/growthChart/infant/InfantWeightLengthChart.jsx